import {
  HStack,
  Pressable,
  Text,
  VStack,
} from '@ayble-health/dashboard-components';
import React, { useCallback, useEffect, useState } from 'react';
import { useToken } from '@chakra-ui/react';
import { CheckCircle, ClipboardList, UserRound, X } from 'lucide-react';
import { UserBlock } from '../../../models/block';
import { PatientSummaryTab } from './PatientSummaryTab';
import { UncoveredGoalsTab } from './UncoveredGoalsTab';

type TabKey = 'patientSummary' | 'uncoveredGoals';

type Props = {
  openEditBlockDrawer: (block: UserBlock) => void;
};

const TABS: { key: TabKey; label: string; Icon: typeof ClipboardList }[] = [
  { key: 'patientSummary', label: 'Patient Summary', Icon: ClipboardList },
  { key: 'uncoveredGoals', label: 'Uncovered Goals', Icon: UserRound },
];

const FEEDBACK_TOAST_DURATION_MS = 5000;

const FeedbackConfirmationToast = ({ onClose }: { onClose: () => void }) => {
  const [grass, canvas] = useToken('colors', ['grass', 'canvas']);

  useEffect(() => {
    const timer = setTimeout(onClose, FEEDBACK_TOAST_DURATION_MS);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <HStack
      aria-live={'polite'}
      role={'status'}
      alignItems={'center'}
      bg={'evergreen'}
      borderRadius={'md'}
      bottom={'24px'}
      boxShadow={'lg'}
      gap={3}
      left={'50%'}
      p={4}
      position={'fixed'}
      transform={'translateX(-50%)'}
      zIndex={1400}
    >
      <CheckCircle color={grass} size={20} />
      <Text color={'canvas'} fontSize={'sm'} whiteSpace={'nowrap'}>
        Your feedback is confirmed. Thanks for helping us make better
        predictions.
      </Text>
      <Pressable aria-label={'Dismiss'} onClick={onClose}>
        <X color={canvas} size={16} />
      </Pressable>
    </HStack>
  );
};

export const PatientOverviewTabsBlock = ({ openEditBlockDrawer }: Props) => {
  const [activeTab, setActiveTab] = useState<TabKey>('uncoveredGoals');
  // Bumped on every save so the toast remounts and restarts its timer.
  const [feedbackToastKey, setFeedbackToastKey] = useState<number | null>(null);
  const showFeedbackToast = useCallback(
    () => setFeedbackToastKey((key) => (key ?? 0) + 1),
    []
  );
  const hideFeedbackToast = useCallback(() => setFeedbackToastKey(null), []);
  const [seafoam, bluegrey] = useToken('colors', ['seafoam', 'bluegrey']);

  return (
    <>
      <VStack
        alignItems={'flex-start'}
        bg={'feather.100'}
        borderColor={'cloud.200'}
        borderWidth={1}
        gap={3}
        p={4}
        rounded={'lg'}
        w={'100%'}
      >
        <HStack
          alignItems={'center'}
          borderBottomWidth={1}
          borderColor={'cloud.200'}
          gap={4}
          mx={-4}
          px={4}
          pb={3}
          w={'calc(100% + 32px)'}
        >
          {TABS.map(({ key, label, Icon }) => {
            const isActive = activeTab === key;

            return (
              <Pressable key={key} mb={-3} onClick={() => setActiveTab(key)}>
                <HStack
                  alignItems={'center'}
                  borderBottomColor={isActive ? 'aqua' : 'transparent'}
                  borderBottomWidth={2}
                  gap={2}
                  pb={3}
                >
                  <Icon color={isActive ? seafoam : bluegrey} size={16} />
                  <Text
                    color={isActive ? 'secondary.500' : 'blueGray.600'}
                    fontSize={'sm'}
                    fontWeight={'semibold'}
                  >
                    {label}
                  </Text>
                </HStack>
              </Pressable>
            );
          })}
        </HStack>

        {activeTab === 'patientSummary' ? (
          <PatientSummaryTab onFeedbackSaved={showFeedbackToast} />
        ) : (
          <UncoveredGoalsTab openEditBlockDrawer={openEditBlockDrawer} />
        )}
      </VStack>

      {feedbackToastKey !== null && (
        <FeedbackConfirmationToast
          key={feedbackToastKey}
          onClose={hideFeedbackToast}
        />
      )}
    </>
  );
};

export default PatientOverviewTabsBlock;
